// src/components/Layout.tsx
import React, { useEffect, useState } from "react";
import { acquireTokenForApi } from "@/authConfig";
import Breadcrumbs from "@/components/Breadcrumbs";
import Header from "@/components/Header";
import SideMenu from "@/components/SideMenu";
import { useNotifications } from "@/hooks/useNotifications";
import NotificationsPanel from "@/components/NotificationsPanel";

const Layout: React.FC<{ children: React.ReactNode; hideSidebar?: boolean; hideBreadcrumbs?: boolean }> = ({ children, hideSidebar = false, hideBreadcrumbs = false }) => {
  const [collapsed, setCollapsed] = useState(false);
  const [panelOpen, setPanelOpen] = useState(false);
  const [sessionGuid, setSessionGuid] = useState<string | null>(null);
  const environment = import.meta.env.VITE_ENVIRONMENT;

  // 📨 Notifications hook
  const { messages, newCount } = useNotifications(sessionGuid);

  // 🎯 Ensure a session is created or reused
  useEffect(() => {
    const ensureSession = async () => {
      const existing = localStorage.getItem("sessionGuid");
      if (existing) {
        setSessionGuid(existing);
        return;
      }

      try {
        const token = await acquireTokenForApi();
        const res = await fetch(`${import.meta.env.VITE_API_URL}/api/activitylog/session/start`, {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
        });

        if (res.ok) {
          const data = await res.json();
          localStorage.setItem("sessionGuid", data.sessionGuid);
          setSessionGuid(data.sessionGuid);
        }
      } catch (err) {
        console.error("Failed to start session", err);
      }
    };

    ensureSession();
  }, []);

  return (
    <>
      <div className="d-flex layout-root">

        {/* 🧭 Sidebar */}
        {!hideSidebar && <aside
          className={`sidebar-wrapper border-end bg-light ${
            collapsed ? "collapsed" : ""
          }`}
        >
          <SideMenu collapsed={collapsed} setCollapsed={setCollapsed} />
        </aside>}

        {/* 🧱 Main content area */}
        <div className="flex-grow-1 position-relative app-main-surface">
          {environment !== "prod" && (
            <div className={`env-banner env-${environment}`}>
                {environment.toUpperCase()} ENVIRONMENT
            </div>
          )}
          {/* Top Header / Navbar */}
          <Header
            acquireTokenForApi={acquireTokenForApi}
            newCount={newCount}
            onTogglePanel={() => setPanelOpen((v) => !v)}
          />

          {/* Main body content */}
          <main
            className="p-3 home-dashboard__main"
            style={{
              marginTop: "60px",
              minHeight: "calc(100vh - 60px)",
            }}
          >
            {!hideBreadcrumbs && <Breadcrumbs />}
            {children}
          </main>
        </div>
      </div>

      {/* 🔔 Notifications overlay */}
      {panelOpen && <div className="message-overlay visible" onClick={() => setPanelOpen(false)} />}

      {/* 🔔 Notifications panel (slide-out) */}
      <NotificationsPanel
        open={panelOpen}
        messages={messages}
        onClose={() => setPanelOpen(false)}
      />

    </>
  );
};

export default Layout;